import React, { useState } from "react";
import {
  getDisplayableImageUrl,
  isRawFormat,
  stabilizeSignedUrl,
} from "@beleef/editor-shot";
import { LiaCommentSolid } from "react-icons/lia";
import axios from "utils/axios";
import Modal from "components/ui/Modal";
import CommentsBox from "components/ui/CommentsBox";

const isPendingComment = (c) =>
  c.status === "revisionSent" ||
  c.status === "revisionInProgress" ||
  (!c.status && !c.responseRevisionId && !c.responseRevisionUrl);

function CommentTrigger({ comments = [], onOpen, className = "" }) {
  if (!comments.length) return null;
  const pending = comments.some(isPendingComment);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold border-0 bg-transparent cursor-pointer p-0 ${
        pending ? "text-red-600" : "text-darkergray"
      } ${className}`}
      title={pending ? "Revision notes" : `Comments (${comments.length})`}
    >
      <span className="relative inline-flex">
        <LiaCommentSolid className="w-3.5 h-3.5" />
        {pending && (
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full" />
        )}
      </span>
      {pending ? "Revision notes" : `Comments (${comments.length})`}
    </button>
  );
}

export default function CaptureRow({
  row,
  onUploadClick,
  onOpenCrop,
  onPreview,
  fileInputRef,
  propertyId,
  eventId,
}) {
  const {
    index,
    captureId,
    shotId,
    rawImage,
    print,
    web,
    cropAdjusted,
    status,
  } = row;

  const [commentsModal, setCommentsModal] = useState(null);

  const rawUrl = getDisplayableImageUrl(rawImage);
  const printUrl = stabilizeSignedUrl(print?.url);
  const webUrl = stabilizeSignedUrl(web?.url);
  const canShowRaw = !!rawUrl && !isRawFormat(rawUrl);
  const slotLabel = rawImage?.name || `Shot ${index + 1}`;
  const rawName = slotLabel;

  const isUploading =
    status === "uploading" ||
    (print?.url && !print?.isUploaded) ||
    (print?.progress > 0 && print?.progress < 100);
  const isGenerating = !!web?.generating || status === "generating";

  const printComments = print?.comments || [];
  const webComments = web?.comments || [];
  const hasPendingPrint = printComments.some(isPendingComment);
  const hasPendingWeb = webComments.some(isPendingComment);
  const hasPendingComments = hasPendingPrint || hasPendingWeb;

  const openComments = async (kind) => {
    const comments = kind === "print" ? printComments : webComments;
    if (!comments.length) return;
    setCommentsModal({
      kind,
      comments,
      title: kind === "print" ? "Print comments" : "Web comments",
    });
    if (!comments.some(isPendingComment) || !propertyId) return;
    try {
      await axios.post("/admin/prepareMarketing/status", {
        eventId,
        propertyId,
        status: "revisionInProgress",
        type: "photo",
      });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  let statusEl;
  if (isUploading) {
    statusEl = (
      <span className="font-mono text-[11px] font-bold uppercase tracking-wide bg-lightgray text-darkgray border border-mediumgray px-2.5 py-1 rounded-full flex items-center gap-1.5">
        <span className="editor-upload-spinner" />
        Uploading
      </span>
    );
  } else if (isGenerating) {
    statusEl = (
      <span className="font-mono text-[11px] font-bold uppercase tracking-wide bg-lightgray text-darkgray border border-mediumgray px-2.5 py-1 rounded-full">
        Generating
      </span>
    );
  } else if (status === "revision" || hasPendingComments) {
    statusEl = (
      <span className="font-mono text-[11px] font-bold uppercase tracking-wide bg-red-600 text-white px-2.5 py-1 rounded-full flex items-center gap-1.5">
        <LiaCommentSolid className="w-3.5 h-3.5" />
        Revision
      </span>
    );
  } else if (status === "ready" && print?.url && web?.url) {
    statusEl = (
      <span className="font-mono text-[11px] font-bold uppercase tracking-wide bg-black text-white px-2.5 py-1 rounded-full flex items-center gap-1.5">
        Ready
      </span>
    );
  } else {
    statusEl = (
      <span className="font-mono text-[11px] font-bold uppercase tracking-wide bg-lightgray text-darkgray border border-mediumgray px-2.5 py-1 rounded-full">
        —
      </span>
    );
  }

  const rowClass = [
    "editor-capture-row",
    status === "revision" || hasPendingComments ? "is-revision" : "",
    status === "ready" ? "is-ready" : "",
    isUploading ? "is-uploading" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rowClass} role="listitem">
      <span className="font-mono text-[11px] font-medium text-darkgray" title={shotId || undefined}>
        {String(index + 1).padStart(2, "0")}
      </span>

      {/* Source */}
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className={`w-14 rounded-md flex-none border border-mediumgray bg-lightgray overflow-hidden ${
            canShowRaw ? "cursor-pointer hover:opacity-90" : ""
          }`}
          style={{ aspectRatio: "4/3" }}
          role={canShowRaw ? "button" : "img"}
          tabIndex={canShowRaw ? 0 : undefined}
          aria-label={rawName}
          onClick={() => {
            if (canShowRaw) onPreview?.("source", index);
          }}
          onKeyDown={(e) => {
            if (canShowRaw && e.key === "Enter") onPreview?.("source", index);
          }}
        >
          {canShowRaw ? (
            <img
              src={rawUrl}
              alt={rawName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : null}
        </div>
        <div className="min-w-0">
          <div className="font-mono font-bold text-[12.5px] truncate">
            {rawName.replace(/\.[^.]+$/, "")}
          </div>
          <div className="text-[11.5px] text-darkgray">
            {rawImage?.type || "source"}
          </div>
        </div>
      </div>

      {/* Print */}
      {print?.url ? (
        <div className="border border-mediumgray rounded-lg min-h-[54px] flex items-center gap-2.5 px-3 py-2 bg-white relative overflow-hidden">
          {isUploading && (
            <div
              className="absolute left-0 bottom-0 h-[3px] bg-black transition-all"
              style={{ width: `${Math.min(100, print.progress || 1)}%` }}
            />
          )}
          <button
            type="button"
            className="w-[34px] h-[34px] rounded-[5px] flex-none bg-lightgray cursor-pointer hover:opacity-90 border-0 p-0"
            style={{
              backgroundImage: printUrl ? `url('${printUrl}')` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            aria-label={`View print ${print.name || captureId}`}
            onClick={() => onPreview?.("print", index)}
          />
          <div className="min-w-0 flex-1">
            <div className="font-mono font-semibold text-[11.5px] truncate max-w-[160px]">
              {print.name || "print"}
            </div>
            <div className="font-mono text-[10.5px] text-darkgray">
              {isUploading
                ? `Uploading · ${Math.round(print.progress || 0)}%`
                : print.size
                  ? `${(print.size / (1024 * 1024)).toFixed(1)} MB · print`
                  : "print master"}
            </div>
            <CommentTrigger
              comments={printComments}
              onOpen={() => openComments("print")}
              className="mt-1"
            />
          </div>
          {!isUploading && !isGenerating && (
            <button
              type="button"
              onClick={() => onUploadClick?.(index)}
              className="text-[11px] font-semibold text-darkgray hover:text-black"
            >
              Replace
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onUploadClick?.(index)}
          className="border border-dashed border-mediumgray rounded-lg min-h-[54px] flex items-center px-3 py-2 w-full text-left hover:border-black transition-colors"
        >
          <span className="text-[12px] text-darkgray">
            Upload print master
          </span>
        </button>
      )}

      {/* Web */}
      {isUploading ? (
        <div className="border border-dashed border-mediumgray rounded-lg min-h-[54px] flex items-center px-3 py-2">
          <span className="text-[12px] text-darkgray">Waiting for print…</span>
        </div>
      ) : isGenerating ? (
        <div className="border border-mediumgray rounded-lg min-h-[54px] flex items-center gap-2.5 px-3 py-2 text-[12px] text-darkgray italic">
          <div className="editor-upload-spinner" />
          Generating web version…
        </div>
      ) : web?.url ? (
        <div className="border border-mediumgray rounded-lg min-h-[54px] flex items-center gap-2.5 px-3 py-2 bg-white">
          <button
            type="button"
            className="flex-none rounded-[5px] border border-mediumgray overflow-hidden p-0 cursor-pointer hover:opacity-90"
            style={{
              width: 56,
              height: 42,
            }}
            title="View web · 1600px · <1MB"
            aria-label={`View web ${captureId}`}
            onClick={() => onPreview?.("web", index)}
          >
            <img
              src={webUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </button>
          <div className="min-w-0 flex-1">
            <div className="font-mono font-semibold text-[11.5px] truncate max-w-[110px]">
              web · {captureId}
            </div>
            <div className="font-mono text-[10.5px] text-darkgray">
              1600px · &lt;1 MB
            </div>
            <CommentTrigger
              comments={webComments}
              onOpen={() => openComments("web")}
              className="mt-1"
            />
          </div>
          <button
            type="button"
            onClick={() => onOpenCrop?.(index)}
            className={[
              "ml-auto border rounded-md px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap flex-none transition-all",
              cropAdjusted
                ? "border-black bg-black text-white"
                : "border-mediumgray bg-white text-black hover:bg-lightgray",
            ].join(" ")}
          >
            {cropAdjusted ? "Adjusted ✓" : "Adjust"}
          </button>
        </div>
      ) : (
        <div className="border border-dashed border-mediumgray rounded-lg min-h-[54px] flex items-center px-3 py-2">
          <span className="text-[12px] text-darkgray">—</span>
        </div>
      )}

      <div className="flex items-center">{statusEl}</div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={isUploading || isGenerating}
        onChange={(e) => {
          const files = e.target.files;
          if (files?.length) onUploadClick?.(index, files);
          e.target.value = "";
        }}
      />

      <Modal
        isOpen={!!commentsModal}
        onClose={() => setCommentsModal(null)}
        title={commentsModal?.title || "Comments"}
      >
        <CommentsBox
          comments={commentsModal?.comments || []}
          imageId={captureId}
        />
        {commentsModal &&
          commentsModal.comments?.some(isPendingComment) &&
          !isUploading &&
          !isGenerating && (
            <div className="mt-4 pt-3 border-t border-mediumgray">
              <p className="text-[12px] text-darkgray mb-2">
                Use Replace on the print master for this shot, then Send to
                Review when all revisions are done.
              </p>
              <button
                type="button"
                onClick={() => {
                  setCommentsModal(null);
                  onUploadClick?.(index);
                }}
                className="w-full py-2.5 px-3 rounded-md bg-black text-white text-[13px] font-semibold hover:opacity-80 border-0 cursor-pointer"
              >
                Replace print master
              </button>
            </div>
          )}
      </Modal>
    </div>
  );
}
